import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Row, Col, Form } from "react-bootstrap";
import ModalHeadAndClose from "../payouts-wtf/common/ModalHeadAndClose";
import { selectToken } from "../auth/auth.selector";
import { fetchApiData } from "../../redux/reducers/commonApiSlice";
import "../../styles/ticket-system/progress-update-modal.css";
import ModalHeadAndCloseV2 from "../payouts-wtf/common/ModalHeadAndCloseV2";

const ProgressUpdateModal = ({
  isOpen,
  onClose,
  ticket,
  onSubmit,
  userInfoData,
}) => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  // Form state
  const [comment, setComment] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [previousProgress, setPreviousProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Ref for progress bar
  const progressBarRef = useRef(null);

  // Calculate progress from mouse/touch position
  const calculateProgress = (clientX) => {
    if (!progressBarRef.current) return progressPercentage;

    const rect = progressBarRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    return Math.round(percentage);
  };

  // Handle progress bar click
  const handleProgressClick = (e) => {
    const newProgress = calculateProgress(e.clientX);
    setProgressPercentage(newProgress);
  };

  // Handle mouse down
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const newProgress = calculateProgress(e.clientX);
    setProgressPercentage(newProgress);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (isDragging) {
      const newProgress = calculateProgress(e.clientX);
      setProgressPercentage(newProgress);
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    setIsDragging(true);
    const touch = e.touches[0];
    const newProgress = calculateProgress(touch.clientX);
    setProgressPercentage(newProgress);

    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (isDragging) {
      const touch = e.touches[0];
      const newProgress = calculateProgress(touch.clientX);
      setProgressPercentage(newProgress);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    setIsDragging(false);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
  };

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  // Fetch previous progress when modal opens
  useEffect(() => {
    if (isOpen && ticket?.uid && token) {
      fetchPreviousProgress();
    }
  }, [isOpen, ticket?.uid, token]);

  const fetchPreviousProgress = async () => {
    try {
      // First check if ticket already has progress_percentage
      const currentProgress = ticket?.progress_percentage || 0;

      if (currentProgress > 0) {
        setPreviousProgress(currentProgress);
        setProgressPercentage(currentProgress);
        return;
      }

      // Fallback: Fetch timeline to get the latest progress percentage
      const timelineResult = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/timeline?ticket_id=${ticket.uid}`,
          token,
        })
      );

      if (
        timelineResult?.payload?.status &&
        Array.isArray(timelineResult.payload.data)
      ) {
        // Find the latest progress update with percentage
        const progressUpdates = timelineResult.payload.data
          .filter(
            (item) =>
              item.progress_percentage !== null &&
              item.progress_percentage !== undefined &&
              item.progress_percentage > 0
          )
          .sort(
            (a, b) =>
              new Date(b.timeline_timestamp) - new Date(a.timeline_timestamp)
          );

        if (progressUpdates.length > 0) {
          const latestProgress = progressUpdates[0].progress_percentage;
          setPreviousProgress(latestProgress);
          setProgressPercentage(latestProgress);
        } else {
          // Use ticket's default progress_percentage
          setPreviousProgress(currentProgress);
          setProgressPercentage(currentProgress);
        }
      } else {
        // Use ticket's default progress_percentage
        setPreviousProgress(currentProgress);
        setProgressPercentage(currentProgress);
      }
    } catch (error) {
      console.error("Error fetching previous progress:", error);
      // Set to ticket's progress_percentage or 0
      const fallbackProgress = ticket?.progress_percentage || 0;
      setPreviousProgress(fallbackProgress);
      setProgressPercentage(fallbackProgress);
    }
  };

  // Validation function
  const validateForm = () => {
    const errors = {};

    if (!comment || comment.trim().length < 10) {
      errors.comment = "Comment must be at least 10 characters";
    }

    if (progressPercentage < 0 || progressPercentage > 100) {
      errors.progressPercentage = "Progress percentage must be between 0-100";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ticket_id: ticket?.uid,
        comment: comment.trim(),
        performed_by: userInfoData?.uid,
      };

      // Add progress percentage if provided
      if (progressPercentage > 0) {
        payload.progress_percentage = parseInt(progressPercentage);
      }

      const result = await dispatch(
        fetchApiData({
          method: "POST",
          endpoint: `/ticketSystem/comment`,
          payload,
          token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message || "Progress updated successfully"
        );
        resetForm();
        onSubmit && onSubmit();
        onClose();
      } else {
        toast.error(result?.payload?.message || "Failed to update progress");
      }
    } catch (error) {
      console.error("Error updating progress:", error);
      toast.error("Failed to update progress");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setComment("");
    setProgressPercentage(previousProgress);
    setFormErrors({});
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Update Progress"
      ariaHideApp={false}
      closeTimeoutMS={300}
      className="progress-update-modal"
      overlayClassName="progress-update-overlay"
    >
      <div className="progress-update-container">
        <div
          style={{
            padding: "10px",
          }}
        >
          <ModalHeadAndCloseV2
            title={`Update Progress - ${ticket?.ticket_no || "TK000"}`}
            subtitle={`${ticket?.gym_name || "WTF Gym"} - ${
              ticket?.issue_type_name || ""
            } - ${ticket?.category_name || ""}`}
            onClose={handleClose}
          />
        </div>

        <div className="modal-body-content">
          {/* Progress Comment */}
          <div className="form-section">
            <label className="section-label required">
              PROGRESS UPDATE COMMENT
            </label>
            <div
              className={`textarea-wrapper ${
                formErrors.comment ? "error" : ""
              }`}
            >
              <Form.Control
                as="textarea"
                rows={4}
                className="progress-textarea"
                placeholder="Describe your current progress, what has been completed, and what remains to be done..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="char-count">{comment.length} characters</div>
              {formErrors.comment && (
                <span className="error-message">{formErrors.comment}</span>
              )}
            </div>
          </div>

          {/* Premium Draggable Progress Bar */}
          <div className="form-section">
            <label className="section-label">
              PROGRESS COMPLETION (optional)
              <span className="progress-value">{progressPercentage}%</span>
            </label>
            <div
              className={`premium-progress-wrapper ${
                formErrors.progressPercentage ? "error" : ""
              }`}
            >
              <div className="premium-progress-header">
                <span className="progress-label">Current Progress</span>
                {previousProgress > 0 &&
                  previousProgress !== progressPercentage && (
                    <span className="previous-indicator">
                      Previous: {previousProgress}%
                    </span>
                  )}
              </div>

              <div
                className="premium-progress-container"
                onClick={handleProgressClick}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                ref={progressBarRef}
              >
                <div className="progress-track">
                  <div
                    className="progress-fill-premium"
                    style={{ width: `${progressPercentage}%` }}
                  >
                    <div className="progress-handle"></div>
                  </div>
                </div>
                <div className="progress-markers">
                  <div
                    className="marker"
                    onClick={() => setProgressPercentage(0)}
                  >
                    0%
                  </div>
                  <div
                    className="marker"
                    onClick={() => setProgressPercentage(25)}
                  >
                    25%
                  </div>
                  <div
                    className="marker"
                    onClick={() => setProgressPercentage(50)}
                  >
                    50%
                  </div>
                  <div
                    className="marker"
                    onClick={() => setProgressPercentage(75)}
                  >
                    75%
                  </div>
                  <div
                    className="marker"
                    onClick={() => setProgressPercentage(100)}
                  >
                    100%
                  </div>
                </div>
              </div>

              <div className="progress-status">
                <span className="status-text">
                  {progressPercentage === 0
                    ? "Not Started"
                    : progressPercentage < 25
                    ? "Initial Phase"
                    : progressPercentage < 50
                    ? "In Progress"
                    : progressPercentage < 75
                    ? "Well Underway"
                    : progressPercentage < 100
                    ? "Almost Complete"
                    : "Completed"}
                </span>
              </div>

              {formErrors.progressPercentage && (
                <span className="error-message">
                  {formErrors.progressPercentage}
                </span>
              )}
            </div>
          </div>

          {/* Current Ticket Info */}
          {/* <div className="ticket-info-section">
            <h4 className="info-title">Current Ticket Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className="info-value">
                  {ticket?.status?.replace("_", " ")}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Priority:</span>
                <span className="info-value">{ticket?.priority}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created:</span>
                <span className="info-value">{ticket?.created_date}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Description:</span>
                <span className="info-value">{ticket?.description}</span>
              </div>
            </div>
          </div> */}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer-buttons">
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={isSubmitting || !comment.trim()}
          >
            {isSubmitting ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></div>
                Updating...
              </>
            ) : (
              "Update Progress"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ProgressUpdateModal;
